import React, { useState } from 'react';
import { WHATSAPP_NUM } from '../constants';

// ---------------------------------------------------------------------------
// 🔐 Security config  –  add these to your .env file:
//   REACT_APP_API_KEY=your-secret-token-here
//   REACT_APP_RECAPTCHA_SITE_KEY=your-recaptcha-site-key
// ---------------------------------------------------------------------------
const API_KEY            = process.env.REACT_APP_API_KEY            || '';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

console.log('CAREERS PAGE CONFIG:', { API_KEY: API_KEY ? '✅' : '❌', RECAPTCHA_SITE_KEY: RECAPTCHA_SITE_KEY ? '✅' : '❌' });

// ---------------------------------------------------------------------------
// reCAPTCHA v3 helper  (same pattern as DemoPage – extract to a shared hook
// if you prefer a single place)
// ---------------------------------------------------------------------------
function useRecaptcha() {
  const loaded = React.useRef(false);

  React.useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || loaded.current) return;
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
    loaded.current = true;
  }, []);

  const getToken = (action) =>
    new Promise((resolve, reject) => {
      if (!RECAPTCHA_SITE_KEY) return resolve(null);
      if (!window.grecaptcha) return reject(new Error('reCAPTCHA not loaded'));
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action })
          .then(resolve)
          .catch(reject);
      });
    });

  return { getToken };
}

// ---------------------------------------------------------------------------
function Field({ label, error, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
      {error && <div className="form-error">⚠️ {error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
export default function CareersPage() {
  const [form, setForm] = useState({
    name: '', skills: '', exp: '', email: '', phone: '', bio: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const { getToken } = useRecaptcha();

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  function validate() {
    const e = {};
    if (!form.name.trim())   e.name   = 'Your name is required';
    if (!form.skills.trim()) e.skills = 'Please list your skills';
    if (!form.email.trim() || !form.email.includes('@'))
                             e.email  = 'Valid email address required';
    if (!form.bio.trim())    e.bio    = 'Please share a bit about yourself';
    return e;
  }

  async function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setStatus('sending');
    setErrors({});

    try {
      // 🔐 Get reCAPTCHA token (returns null if site key not configured)
      const recaptchaToken = await getToken('career_application');

      const sharedParams = {
        applicant_name:   form.name,
        applicant_email:  form.email,
        applicant_phone:  form.phone || 'Not provided',
        applicant_skills: form.skills,
        applicant_exp:    form.exp   || 'Not specified',
        applicant_bio:    form.bio,
        submitted_at:     new Date().toLocaleString('en-IN'),
        recaptchaToken,   // sent to Cloud Function for server-side verification
      };

      const res = await fetch(
        'https://us-central1-remote-camera-control.cloudfunctions.net/sendCareerEmail',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,  // 🔐 shared secret
          },
          body: JSON.stringify(sharedParams),
        }
      );

      if (!res.ok) throw new Error('Failed');
      setStatus('success');

    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  }

  // ── Success screen ───────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div style={{
        paddingTop: 64, minHeight: '70vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center', background: 'var(--cream)',
      }}>
        <div style={{ textAlign: 'center', padding: '40px 24px', maxWidth: 500 }}>
          <div style={{ fontSize: 70, marginBottom: 16 }}>🎉</div>
          <h2 style={{ color: 'var(--navy)', fontWeight: 900, fontSize: 28, marginBottom: 12 }}>
            Application Received!
          </h2>
          <p style={{ color: 'var(--gray)', fontSize: 16, lineHeight: 1.75 }}>
            Thank you, <strong>{form.name}</strong>! We've sent a confirmation to{' '}
            <strong>{form.email}</strong>. We'll review your application and reach
            out within 3–5 business days.
          </p>
          <button
            className="btn-secondary"
            style={{ marginTop: 28 }}
            onClick={() => {
              setStatus('idle');
              setForm({ name: '', skills: '', exp: '', email: '', phone: '', bio: '' });
            }}
          >
            Submit Another Application
          </button>
        </div>
      </div>
    );
  }

  // ── Page ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingTop: 64 }}>

      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #0d2045, #1B3A6B, #2A5298)',
        padding: '72px 24px 56px', color: '#fff', textAlign: 'center',
      }}>
        <div className="badge" style={{
          background: 'rgba(240,180,41,0.2)', color: '#F0B429',
          border: '1.5px solid rgba(240,180,41,0.38)',
        }}>
          💼 We're Hiring!
        </div>
        <h1 className="serif" style={{
          fontSize: 'clamp(26px, 5vw, 46px)', fontWeight: 800, margin: '18px 0 14px',
        }}>
          Join as a Coding Instructor
        </h1>
        <p style={{
          color: 'rgba(255,255,255,0.82)', fontSize: 17,
          maxWidth: 560, margin: '0 auto', lineHeight: 1.7,
        }}>
          Share your coding knowledge, earn from home, and help the next generation
          of students build their futures.
        </p>
        <div style={{
          display: 'flex', gap: 16, justifyContent: 'center',
          flexWrap: 'wrap', marginTop: 36,
        }}>
          {['🏠 Work from home', '⏰ Part-time & flexible', '💰 Competitive pay', '📜 Teaching certificate'].map(t => (
            <span key={t} style={{
              background: 'rgba(255,255,255,0.12)', padding: '9px 20px',
              borderRadius: 50, fontSize: 14, fontWeight: 700,
              border: '1px solid rgba(255,255,255,0.2)',
            }}>{t}</span>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="section">
        <div className="container">
          <div className="grid-2" style={{ gap: 48 }}>

            {/* Requirements */}
            <div>
              <div className="badge">📋 What We Look For</div>
              <h2 className="section-title">Requirements</h2>
              {[
                ['🎯', 'Technical Skills',    "Proficiency in Python or Web Development (HTML, CSS, JavaScript). You don't need to know everything — just one area deeply."],
                ['❤️',     'Passion for Teaching', 'Genuine enjoyment in explaining concepts and seeing students grow and succeed.'],
                ['🗣️',    'Clear Communication',   'Ability to explain complex ideas in simple, friendly, easy-to-understand language.'],
                ['💻',     'Equipment',             'A working laptop and stable internet connection for live online sessions.'],
                ['⏰',     'Time Availability',     'Minimum 4–6 hours per week, across 3 session days. Flexible timing negotiable.'],
                ['🤝',     'Patience & Commitment', 'Our students are beginners — patience and consistent commitment are essential.'],
              ].map(([icon, title, desc]) => (
                <div key={title} style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
                  <div style={{ fontSize: 22, minWidth: 28, marginTop: 2 }}>{icon}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 3 }}>{title}</div>
                    <div style={{ color: 'var(--gray)', fontSize: 14, lineHeight: 1.65 }}>{desc}</div>
                  </div>
                </div>
              ))}

              {/* Benefits */}
              <div style={{
                background: 'var(--gold-light)', border: '2px solid var(--gold)',
                borderRadius: 18, padding: 24, marginTop: 8,
              }}>
                <h3 style={{ color: 'var(--navy)', fontWeight: 800, fontSize: 17, marginBottom: 14 }}>
                  📦 What We Offer
                </h3>
                {[
                  'Part-time, flexible schedule you control',
                  'Work 100% from home — online sessions',
                  'Competitive per-session pay',
                  'Teaching experience certificate',
                  'Growing academy — grow with us',
                  'Supportive, collaborative team environment',
                ].map(t => (
                  <div key={t} style={{ display: 'flex', gap: 8, marginBottom: 9, fontSize: 14, color: 'var(--navy)' }}>
                    <span style={{ color: 'var(--crimson)', fontWeight: 900 }}>✓</span>{t}
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="card" style={{ padding: '40px 36px' }}>
              <h3 style={{ color: 'var(--navy)', fontWeight: 900, fontSize: 22, marginBottom: 6 }}>
                📝 Apply Now
              </h3>
              <p style={{ color: 'var(--gray)', fontSize: 14, marginBottom: 28 }}>
                Takes less than 5 minutes. We review every application carefully.
              </p>

              {status === 'error' && (
                <div style={{
                  background: '#fff0f0', border: '1.5px solid #f5c6c6',
                  borderRadius: 10, padding: '12px 16px', marginBottom: 20,
                  color: '#c0392b', fontSize: 14,
                }}>
                  ⚠️ Something went wrong sending your application. Please try again or
                  WhatsApp us directly.
                </div>
              )}

              <Field label="Your Full Name *" error={errors.name}>
                <input className="form-input" placeholder="What should I call you?"
                  value={form.name} onChange={set('name')} />
              </Field>

              <Field label="Technical Skills *" error={errors.skills}>
                <input className="form-input" placeholder="e.g. Python, HTML, CSS, JavaScript, React"
                  value={form.skills} onChange={set('skills')} />
              </Field>

              <Field label="Years of Experience">
                <input className="form-input" placeholder="e.g. 2 years"
                  value={form.exp} onChange={set('exp')} />
              </Field>

              <Field label="Email Address *" error={errors.email}>
                <input className="form-input" type="email" placeholder="your@email.com"
                  value={form.email} onChange={set('email')} />
              </Field>

              <Field label="Phone / WhatsApp (Optional)">
                <input className="form-input" type="tel" placeholder="10-digit mobile number"
                  value={form.phone} onChange={set('phone')} />
              </Field>

              <Field label="Why do you want to teach at VRK Tech Academy? *" error={errors.bio}>
                <textarea className="form-input" rows={4}
                  placeholder="Tell us about your experience, teaching style, and why this role excites you..."
                  value={form.bio} onChange={set('bio')} style={{ resize: 'vertical' }} />
              </Field>

              <button
                className="btn-primary"
                style={{
                  width: '100%', padding: '16px', fontSize: 16,
                  opacity: status === 'sending' ? 0.7 : 1,
                  cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                }}
                onClick={handleSubmit}
                disabled={status === 'sending'}
              >
                {status === 'sending' ? '⏳ Sending…' : 'Submit Application →'}
              </button>

              <div style={{
                marginTop: 20, textAlign: 'center',
                color: 'var(--gray)', fontSize: 13, lineHeight: 1.65,
              }}>
                Or send your resume directly on WhatsApp:&nbsp;
                <span
                  style={{ color: 'var(--navy)', fontWeight: 700, cursor: 'pointer' }}
                  onClick={() => window.open(
                    `https://wa.me/${WHATSAPP_NUM}?text=${encodeURIComponent('Hi! I am interested in the Coding Instructor role at VRK Tech Academy.')}`,
                    '_blank',
                  )}
                >
                  Click here to WhatsApp →
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}